import OrderRecovery from "@/components/OrderRecovery";
import { sendOrder } from "@/utils/orderCheckout";
import { useEffect, useMemo, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useCart } from "@/contexts/CartContext";
import api, { formatApiError } from "@/utils/api";
import { fmtCurrency, fmt } from "@/utils/format";
import { B2B } from "@/constants/testIds";
import { toast } from "sonner";
import { Minus, Plus, Trash2, ShoppingBag, Package, CheckSquare, Square } from "lucide-react";

export default function B2BSepet() {
  const { user } = useAuth();
  const { cart, updateQuantity, removeFromCart, removeCartItems, clearCart, refreshCartItems } = useCart();
  const [not, setNot] = useState("");
  const submittingRef = useRef(false);
  const [submitting, setSubmitting] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set(cart.map((item) => item.stok_id)));
  const cartStockIds = useMemo(() => cart.map((item) => item.stok_id).join(","), [cart]);
  const navigate = useNavigate();

  const selectedCart = cart.filter((item) => selectedIds.has(item.stok_id));
  const selectedTotal = selectedCart.reduce((sum, item) => sum + item.birim_fiyat * item.miktar, 0);
  const allSelected = cart.length > 0 && selectedCart.length === cart.length;

  useEffect(() => {
    setSelectedIds((previous) => new Set([...previous].filter((id) => cart.some((item) => item.stok_id === id))));
  }, [cart]);

  useEffect(() => {
    if (!cartStockIds) return;
    api.post("/stoklar/b2b/cart-refresh", { stok_ids: cartStockIds.split(",").filter(Boolean) })
      .then((response) => refreshCartItems(response.data.items || []))
      .catch(() => toast.error("Sepetteki güncel fiyatlar alınamadı; sipariş sırasında tekrar kontrol edilecek"));
  }, [cartStockIds, refreshCartItems]);

  const handleOrder = async (bakiyeOnayi = false) => {
    if (!selectedCart.length) { toast.error("Sipariş vermek için en az bir ürün seçin"); return; }
    if (!user?.cari_id) { toast.error("Hesabınıza bir cari kodu atanmamış, lütfen yöneticinizle iletişime geçin"); return; }

    if (submittingRef.current) return;
    submittingRef.current = true;
    setSubmitting(true);
    try {
      const satirlar = selectedCart.map((item) => ({
        stok_id: item.stok_id,
        stok_kodu: item.stok_kodu,
        stok_adi: item.stok_adi,
        miktar: item.miktar,
        birim_fiyat: item.birim_fiyat,
        toplam: item.birim_fiyat * item.miktar,
      }));

      const r = await sendOrder(user.id, {
        cari_id: user.cari_id,
        satirlar,
        not_: not || null,
        bakiye_onayi: bakiyeOnayi,
      });

      const submittedIds = selectedCart.map((item) => item.stok_id);
      const hasRemainingItems = selectedCart.length < cart.length;
      removeCartItems(submittedIds);
      if (r.data.fiyat_guncellendi) {
        toast.info("Sepette bekleyen ürünler güncel kur ve fiyatlarla yeniden hesaplandı");
      }
      toast.success(`Sipariş oluşturuldu: ${r.data.siparis_no}`);
      if (hasRemainingItems) {
        setNot("");
        toast.info("Seçilmeyen ürünler sonraki siparişiniz için sepette bırakıldı");
      } else {
        navigate("/b2b/anasayfa");
      }
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (e.response?.status === 409 && detail?.code === "INSUFFICIENT_STOCK_CONFIRMATION") {
        const lines = (detail.items || []).map((item) => `${item.stok_kodu}: ${item.istenen} istendi, ${item.bakiyeye_alinacak} bakiyeye`).join("\n");
        if (window.confirm(`${detail.message}\n\n${lines}`)) {
          submittingRef.current = false; setSubmitting(false);
          await handleOrder(true);
          return;
        }
        return;
      }
      toast.error(formatApiError(e));
    } finally {
      submittingRef.current = false; setSubmitting(false);
    }
  };

  if (cart.length === 0) {
    return (
      <div className="p-4 flex flex-col items-center justify-center min-h-[60vh] text-center">
        <div className="w-16 h-16 bg-slate-100 rounded-2xl flex items-center justify-center mb-4">
          <ShoppingBag size={28} className="text-slate-400" />
        </div>
        <OrderRecovery userId={user?.id} disabled={submitting}/><h2 className="text-lg font-semibold text-slate-700 mb-2">Sepetiniz Boş</h2>
        <p className="text-sm text-slate-400 mb-6">Stok listesinden ürün ekleyebilirsiniz</p>
        <button onClick={() => navigate("/b2b/stok")}
          className="flex items-center gap-2 bg-[#0D1B3E] text-white rounded-lg px-5 py-2.5 text-sm font-medium hover:bg-[#172A58] transition-colors">
          <Package size={15} /> Stok Listesi
        </button>
      </div>
    );
  }

  return (
    <div className="p-4 space-y-4">
      <div className="flex items-center justify-between">
        <OrderRecovery userId={user?.id} disabled={submitting}/><h1 className="text-lg font-bold text-[#0D1B3E]" style={{ fontFamily: "Outfit, sans-serif" }}>
          Sepetim ({cart.length} ürün)
        </h1>
        <button onClick={clearCart} className="text-xs text-red-500 hover:text-red-700 transition-colors">
          Temizle
        </button>
      </div>

      <button
        type="button"
        onClick={() => setSelectedIds(allSelected ? new Set() : new Set(cart.map((item) => item.stok_id)))}
        className="flex items-center gap-2 text-sm font-semibold text-[#0D1B3E]"
      >
        {allSelected ? <CheckSquare size={17}/> : <Square size={17}/>} {allSelected ? "Tüm seçimleri kaldır" : "Tümünü seç"}
      </button>

      {/* Cart Items */}
      <div data-testid={B2B.cartTable} className="space-y-2">
        {cart.map((item) => (
          <div key={item.stok_id} className={`bg-white border rounded-lg p-4 shadow-sm transition-colors ${selectedIds.has(item.stok_id) ? "border-[#0D1B3E]/40" : "border-slate-200 opacity-70"}`}>
            <div className="flex items-start justify-between gap-3">
              <button type="button" onClick={() => setSelectedIds((previous) => { const next = new Set(previous); next.has(item.stok_id) ? next.delete(item.stok_id) : next.add(item.stok_id); return next; })} aria-label={`${item.stok_kodu} ürününü ${selectedIds.has(item.stok_id) ? "seçimden çıkar" : "seç"}`} className="mt-0.5 text-[#0D1B3E] shrink-0">
                {selectedIds.has(item.stok_id) ? <CheckSquare size={18}/> : <Square size={18}/>}
              </button>
              <div className="flex-1 min-w-0">
                <div className="font-mono text-xs text-slate-400">{item.stok_kodu}</div>
                <div className="font-medium text-slate-800 text-sm mt-0.5 truncate">{item.stok_adi}</div>
                <div className="text-xs text-slate-400 mt-0.5">{fmtCurrency(item.birim_fiyat)} / adet</div>
              </div>
              <button onClick={() => removeFromCart(item.stok_id)} className="text-slate-300 hover:text-red-500 transition-colors shrink-0">
                <Trash2 size={15} />
              </button>
            </div>
            <div className="flex items-center justify-between mt-3">
              <div className="flex items-center gap-2">
                <button onClick={() => updateQuantity(item.stok_id, item.miktar - Math.max(1, Number(item.paket_adedi) || 1))}
                  className="w-7 h-7 rounded-lg border border-slate-300 flex items-center justify-center text-slate-600 hover:bg-slate-100 transition-colors">
                  <Minus size={12} />
                </button>
                {/* Doğrudan yazılabilir miktar alanı */}
                <input
                  type="number"
                  min={Math.max(1, Number(item.paket_adedi) || 1)}
                  step={Math.max(1, Number(item.paket_adedi) || 1)}
                  value={item.miktar}
                  onChange={(e) => {
                    const val = parseInt(e.target.value, 10);
                    if (val > 0) updateQuantity(item.stok_id, val);
                  }}
                  onClick={(e) => e.target.select()}
                  className="w-12 h-7 text-center text-sm font-semibold border border-slate-200 rounded-lg focus:outline-none focus:border-[#0D1B3E] focus:ring-1 focus:ring-[#0D1B3E]/20 tabular-nums bg-white"
                />
                <button onClick={() => updateQuantity(item.stok_id, item.miktar + Math.max(1, Number(item.paket_adedi) || 1))}
                  className="w-7 h-7 rounded-lg border border-slate-300 flex items-center justify-center text-slate-600 hover:bg-slate-100 transition-colors">
                  <Plus size={12} />
                </button>
              </div>
              {Number(item.paket_adedi || 1) > 1 && <div className="mt-1 text-[10px] font-medium text-amber-600">Paket: {item.paket_adedi} adet ve katları</div>}
              <div className="font-bold text-[#0D1B3E] tabular-nums">
                {fmtCurrency(item.birim_fiyat * item.miktar)}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Note */}
      <div className="bg-white border border-slate-200 rounded-lg p-4">
        <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Sipariş Notu (opsiyonel)</label>
        <textarea
          value={not}
          onChange={(e) => setNot(e.target.value)}
          rows={2}
          placeholder="Özel talep veya not ekleyin..."
          className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0D1B3E]/20 resize-none"
        />
      </div>

      {/* Summary */}
      <div className="bg-white border border-slate-200 rounded-lg p-4 shadow-sm">
        <div className="space-y-2 mb-4">
          {selectedCart.map((item) => (
            <div key={item.stok_id} className="flex justify-between text-xs text-slate-600">
              <span className="truncate flex-1 mr-2">{item.stok_kodu} × {item.miktar}</span>
              <span className="tabular-nums shrink-0">{fmtCurrency(item.birim_fiyat * item.miktar)}</span>
            </div>
          ))}
          <div className="border-t border-slate-200 pt-2 flex justify-between font-bold text-slate-800">
            <span>Toplam</span>
            <span className="tabular-nums text-[#0D1B3E]">{fmtCurrency(selectedTotal)}</span>
          </div>
        </div>

        <button
          data-testid={B2B.orderSubmit}
          onClick={() => handleOrder(false)}
          disabled={submitting || selectedCart.length === 0}
          className="w-full bg-[#0D1B3E] text-white rounded-lg py-3 font-semibold text-sm hover:bg-[#172A58] active:scale-[0.98] disabled:opacity-60 transition-all"
        >
          {submitting ? "Sipariş Oluşturuluyor..." : `Seçilenleri Sipariş Ver — ${fmtCurrency(selectedTotal)}`}
        </button>
        <p className="text-xs text-slate-400 text-center mt-2">Siparişiniz onaylandıktan sonra işleme alınacaktır</p>
      </div>
    </div>
  );
}
